import { action, computed, makeAutoObservable } from 'mobx';

import { Common } from 'src/AppState/Common';
import { AutoMap } from 'src_common/common/mobx-utils/AutoMap';
import { Resource } from 'src_common/common/mobx-utils/Resource';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import { PaginationConfigType } from 'src/domains/casino/webview/bonus-engine/common/components/pagination-table/PaginationTable';
import { ApiGetOptedInAccountsResponse200Type } from 'src/api_openapi/v2/bonus-engine/apiGetOptedInAccounts';
import { RouterBackoffice } from 'src/AppState/RouterBackoffice';
import { generateCsv, mkConfig, download } from 'export-to-csv';
import { DateTime } from 'src_common/utils/time/time';

const pageSizeOptions: number[] = [10, 25, 50, 100];

interface SelectValueType {
    value: string;
    label: string;
    onClick: () => void;
}

export class OptedInAccountsDialogState {
    public optedInUsersResource: AutoMap<string, Resource<ApiGetOptedInAccountsResponse200Type>>;
    public isOptInDialogShown: boolean = false;
    public search: FormInputState<string, string> = FormInputState.new('');
    public sortConfig: { key: 'accountId' | 'createdAt'; direction: 'ascending' | 'descending' } | null = null;
    private currentPage: number = 1;
    private rowsPerPage: number = 10;
    private rowsPerPageState = FormInputState.new('10');
    private campaignId: string;
    private routerBackoffice: RouterBackoffice;

    public constructor(
        private common: Common,
        routerBackoffice: RouterBackoffice,
        campaignId: string
    ) {
        this.optedInUsersResource = new AutoMap((campaignId: string) => {
            return new Resource(async (): Promise<ApiGetOptedInAccountsResponse200Type> => {
                const response = await this.common.trpcClient.client.bonusCampaignsRouter.getOptedInUsers.mutate({
                    campaignId,
                });

                if (response.status === 200) {
                    return response.body;
                }
                return [];
            });
        });

        this.campaignId = campaignId;
        this.routerBackoffice = routerBackoffice;

        makeAutoObservable(this);
    }

    public onGenerateCSV = (): void => {
        const headers = [
            { key: 'accountId', displayLabel: 'Account Id' },
            { key: 'optedInTimestamp', displayLabel: 'Opted-in Timestamp (UK time)' },
        ];
        const options = {
            quoteStrings: true,
            title: 'Opted in Players',
            filename: 'Opted-in-Players-Report',
            columnHeaders: headers,
        };
        const csvExporter = mkConfig(options);
        const csvData = this.filteredData.map((progress) => ({
            accountId: progress.accountId,
            optedInTimestamp: DateTime.from(progress.createdAt)?.format('YYYY-MM-DD HH:mm:ss'),
        }));
        const csv = generateCsv(csvExporter)(csvData);
        download(csvExporter)(csv);
    };

    @computed public get optedInUsers(): ApiGetOptedInAccountsResponse200Type | 'loading' | 'error' {
        if (this.isOptInDialogShown === false) {
            return [];
        }

        const res = this.optedInUsersResource.get(this.campaignId).get();

        if (res.type === 'loading') {
            return 'loading';
        }

        if (res.type === 'error') {
            return 'error';
        }

        return res.value;
    }

    @computed public get filteredData(): ApiGetOptedInAccountsResponse200Type {
        const optedInUsers = this.optedInUsers;

        if (optedInUsers === 'loading' || optedInUsers === 'error') {
            return [];
        }

        const data = this.filterAccounts(optedInUsers);

        return this.sortAccounts(data);
    }

    @computed public get isLoading(): boolean {
        return this.optedInUsers === 'loading' || this.isOptInDialogShown === false;
    }

    @computed public get paginationConfig(): PaginationConfigType<string> {
        return {
            currentPage: this.currentPage,
            endItem: this.endItem,
            setPage: this.setPage,
            startItem: this.startItem,
            totalItems: this.totalItems,
            totalPages: this.totalPages,
            rowsPerPageState: this.rowsPerPageState,
            selectValues: this.selectValues,
        };
    }

    public get selectValues(): SelectValueType[] {
        return pageSizeOptions.map((size) => ({
            value: size.toString(),
            label: size.toString(),
            onClick: () => this.setRowsPerPage(size),
            dataTest: `page-size-${size}`,
        }));
    }

    @computed public get paginatedData(): ApiGetOptedInAccountsResponse200Type {
        const data = this.filteredData;

        const startIndex = (this.currentPage - 1) * this.rowsPerPage;
        return data.slice(startIndex, startIndex + this.rowsPerPage);
    }

    @computed public get totalPages(): number {
        return Math.max(1, Math.ceil(this.filteredData.length / this.rowsPerPage));
    }

    @computed public get totalItems(): number {
        return this.filteredData.length;
    }

    @computed public get startItem(): number {
        if (this.totalItems === 0) {
            return 0;
        }
        return (this.currentPage - 1) * this.rowsPerPage + 1;
    }

    @computed public get endItem(): number {
        return Math.min(this.startItem + this.rowsPerPage - 1, this.totalItems);
    }

    @action public showOptInDialog = (show: boolean): void => {
        this.currentPage = 1;
        this.isOptInDialogShown = show;
    };

    @action public setPage = (page: number): void => {
        this.currentPage = Math.max(1, Math.min(page, this.totalPages));
    };

    @action public setRowsPerPage = (rows: number): void => {
        this.rowsPerPage = rows;
        this.rowsPerPageState.setValue(rows.toString());
        this.currentPage = 1;
    };

    @action public setSearch = (value: string): void => {
        this.search.setValue(value);
        this.currentPage = 1;
    };

    @action public setSortConfig = (key: 'accountId' | 'createdAt'): void => {
        if (this.sortConfig !== null && this.sortConfig.key === key) {
            this.sortConfig.direction = this.sortConfig.direction === 'ascending' ? 'descending' : 'ascending';
        } else {
            this.sortConfig = { key, direction: 'ascending' };
        }
    };

    @action public redirectToAccount = (accountId: number): void => {
        this.routerBackoffice.redirectToNamespace(`customers:${accountId}`, {
            tab: 'customer',
            'tab.tab': 'customer-promotion-history',
        });
        this.showOptInDialog(false);
    };

    private filterAccounts(data: ApiGetOptedInAccountsResponse200Type): ApiGetOptedInAccountsResponse200Type {
        const filterText = this.search.value.toLowerCase();
        let filteredData = data;

        if (filterText !== '') {
            filteredData = data.filter((progress) => {
                return progress.accountId.toString().toLowerCase().includes(filterText);
            });
        }

        return filteredData;
    }

    private sortAccounts(data: ApiGetOptedInAccountsResponse200Type): ApiGetOptedInAccountsResponse200Type {
        if (this.sortConfig === null) {
            return data;
        }

        const { key, direction } = this.sortConfig;

        return [...data].sort((a, b) => {
            const getValue = (
                progress: ApiGetOptedInAccountsResponse200Type[number],
                key: keyof ApiGetOptedInAccountsResponse200Type[number]
            ): string | number | undefined => {
                const value = progress[key];

                return typeof value === 'string' || typeof value === 'number' ? value : undefined;
            };

            const aValue = getValue(a, key);
            const bValue = getValue(b, key);

            if (typeof aValue === 'string' && typeof bValue === 'string') {
                return direction === 'ascending' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
            } else if (typeof aValue === 'number' && typeof bValue === 'number') {
                return direction === 'ascending' ? aValue - bValue : bValue - aValue;
            }

            return 0;
        });
    }
}
